import SendIcon from '@mui/icons-material/Send';
import {
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  Tab,
} from "@mui/material";
import Box from '@mui/material/Box';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Tabs from '@mui/material/Tabs';
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Mention, MentionsInput } from 'react-mentions';
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import HtmlContentRenderer from "../../components/HtmlContentRenderer";
import OpenProjectCreateTaskDialog from "../../components/openProject/createOpenProjectTaskDialog";
import AdditionalInfoTaskDialog from "../../components/task/additionalInfoDialog";
import AddMeetingDialog from "../../components/task/addMeetingDialog";
import CreateJiraTaskDialog from "../../components/task/createJiraTaskDialog";
import UpdateTaskStatusDialog from "../../components/task/updateStatusDialog";
import defaultAvatar from "../../icons/userDefaultAvatar.svg";
import { addJiraCommentForTask, getAttachment, getAttachmentsAndComments, getIssueActions, getJiraTaskCommentDetails, getJiraTaskDetails, getJiraUser } from '../../services/jira.service';
import { addOpenProjectComment, getOpenProjectComments, searchOpenProjectUser } from "../../services/openProject.service";
import { setOpenCreateDocumentation } from "../../store/documentSlice";
import { setNewComments } from "../../store/IssueSlice";
import { RootState } from "../../store/store";
import { ProjectContext } from "../Homepage";
import "./style.scss";
import JiraIssueDescriptionRenderer from "../../components/JiraIssueDescriptionRenderer";
import { IComment, ICommentField, IJiraIssue, IOpenProjectComment, IOpenProjectUserDetails } from "../../common/interface";
import { AttachmentProvider } from "../../context/AttachmentContext";
import JiraAssigneeSelector from "../../components/JiraAssigneeSelector";
import OpenProjectAssigneeSelector from "../../components/OpenProjectAssigneeSelector";
import CommentsSection from "../../components/CommentsSection";
import OPCommentSection from "../../components/OPCommentSection";

interface IssueDetailsPageProps {
    setCreatedMeetingId: React.Dispatch<React.SetStateAction<string | null>>
    setIssueRefetch: React.Dispatch<React.SetStateAction<boolean>>
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            className="tab-panel"
        >
            {value === index && (
                <Box style={{ height: 'calc(100% - 40px)' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const IssueDetailsPage = ({ setCreatedMeetingId, setIssueRefetch }: IssueDetailsPageProps) => {
    const issueDetails = useSelector((state: RootState) => state.issue, shallowEqual);
    const projectState = useSelector((state: RootState) => state.project, shallowEqual);
    const userState = useSelector((state: RootState) => state.user, shallowEqual);
    const selectedProject = projectState?.selectedProject;
    const projectTypeMenu = projectState.projectTypeMenu
    const projectType = projectState.projectType
    const { selectedNotification } = useContext(ProjectContext);

    const [commandsList, setCommandsList] = useState<any[]>([]);
    const [meetScheduleList, setMeetScheduleList] = useState<any[]>([]);

    const [additionalInfoDialogOpen, setAdditionalInfoDialogOpen] = useState(false)
    const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false)
    const [createJiraSubTaskDialogOpen, setCreateJiraSubTaskDialogOpen] = useState<boolean>(false);
    const [createOpenProjectSubTaskDialogOpen, setCreateOpenProjectSubTaskDialogOpen] = useState<boolean>(false);
    const [addMeetingDialogOpen, setAddMeetingDialogOpen] = useState(false)
    const [feedBackActions, setFeedBackActions] = useState<any[]>([]);
    const [tabValue, setTabValue] = useState(0);
    const [commentText, setCommentText] = useState('');
    const dispatch = useDispatch();
    const scrollableDivRef = useRef<HTMLDivElement | null>(null);
    const [fullScreenLoading, setFullScreenLoading] = useState(false);
    const [blockParent, setBlockParent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mentionMapping, setMentionMapping] = useState<Record<string, string>>({});
    const [matchLength, setMatchLength] = useState<number>(0)
    const [openProjectUserlist, setOpenProjectUserlist] = useState<any>([])
    const [selectedTaskDetails, setSelectedTaskDetails] = useState<IJiraIssue | null>(null);
    const [fetchingJiraTask, setFetchingJiraTask] = useState<boolean>(false);
    const [fetchingJiraComment, setFetchingJiraComment] = useState<boolean>(false);
    const [jiraTaskComments, setJiraTaskComments] = useState<IComment[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const toggleFullScreen = useCallback(() => {
      if (!document.fullscreenElement && containerRef.current) {
        containerRef.current.requestFullscreen().catch((err) => {
          console.error(
            `Error attempting to enable fullscreen: ${err.message}`
          );
        });
      } else if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }, []);

    useEffect(() => {
      const handleFullscreenChange = () => {
        setIsFullScreen(document.fullscreenElement === containerRef.current);
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
      };
    }, []);

    const handleMentionAdd = (id: string | number, display?: string) => {

        if (typeof id === "string" && display) {
            setMentionMapping((prev) => ({ ...prev, [id]: display }));
        }
    };

    const openCreateSubTaskDialog = () => {
        switch (projectType) {
            case (projectTypeMenu[0]): {
                setCreateOpenProjectSubTaskDialogOpen(true);
                break;
            }
            case (projectTypeMenu[2]): {
                setCreateJiraSubTaskDialogOpen(true);
                break;
            }
        }
    }
    const closeCreateSubTaskDialog = (isTaskCreated: boolean = true) => {
        switch (projectType) {
            case (projectTypeMenu[0]): {
                setCreateOpenProjectSubTaskDialogOpen(false);
                setIssueRefetch(isTaskCreated);
                break;
            }
            case (projectTypeMenu[2]): {
                setCreateJiraSubTaskDialogOpen(false);
                setIssueRefetch(isTaskCreated);
                break;
            }
        }
    }

    const openUpdateStatusDialog = () => {
        setUpdateStatusDialogOpen(true)
    }
    const closeUpdateStatusDialog = (statusChanged?: boolean) => {
        if (statusChanged) {
            setIssueRefetch(true);
        }
        setUpdateStatusDialogOpen(false)
    }
    const closeAdditionalInfoDialog = () => {
        setAdditionalInfoDialogOpen(false)
    }

    const openAddMeetingDialog = () => {
        setAddMeetingDialogOpen(true);
    };
    const closeAddMeetingDialog = (
        isMeetingCreated: boolean = false,
        meetingTaskId?: string
    ) => {
        if (isMeetingCreated) {
            if (isMeetingCreated && meetingTaskId) {
                setCreatedMeetingId(meetingTaskId);
            } else {
                setIssueRefetch(true);
            }
            setAddMeetingDialogOpen(false);
        } else {
            setAddMeetingDialogOpen(false);
        }
    }

    const showTaskFeature = useMemo(() => {
        switch (projectType) {
            case (projectTypeMenu[0]): {
                return true;
            }
            case (projectTypeMenu[2]): {
                return true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectType])

    const getOpenProjectCommands = async () => {
      setLoading(true);
      try {
        if (!issueDetails?.key) {
          console.warn(
            "No issue key available to fetch Open Project comments."
          );
          setCommandsList([]);
          return;
        }
        const commentResp = await getOpenProjectComments(issueDetails.key);
        const fetchedComments: IOpenProjectComment[] =
          commentResp?.data?.comments || [];
        setCommandsList(fetchedComments);
      } catch (error) {
        console.error("Failed to fetch Open Project commands:", error);
        setCommandsList([]);
      } finally {
        setLoading(false);
      }
    };

    const getWaymoreCommands = async () => {
        setCommandsList([]);
        setMeetScheduleList([]);
    }

    const fetchTaskComments = async (
      taskSelfUrl: string,
      signal: AbortSignal
    ) => {
      setFetchingJiraComment(true);
      setJiraTaskComments([]);
      if (!taskSelfUrl) {
        return;
      }
      try {
        const commentsRes = (await getJiraTaskCommentDetails(
          taskSelfUrl,
          signal
        )) as unknown as ICommentField;
        if (commentsRes?.comments?.length) {
          setJiraTaskComments(commentsRes.comments);
        }
      } catch {
        toast.error(
          `Failed to fetch the comments for the Issue ${issueDetails?.key}`
        );
      } finally {
        setFetchingJiraComment(false);
      }
    };
    const getSelectedTaskDetails = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const signal = controller.signal;
      setSelectedTaskDetails(null);
      setJiraTaskComments([]);
      try {
        if (!issueDetails?.key) {
          console.warn("Issue key not found");
          setFetchingJiraTask(false);
          setFetchingJiraComment(false);
          return;
        }
        setFetchingJiraTask(true);
        setFetchingJiraComment(true);
        const { data } = await getJiraTaskDetails(issueDetails.key, signal);
        fetchTaskComments(data?.self, signal);
        setSelectedTaskDetails(data);
      } catch (error: unknown) {
        console.error(
          "[Error] in fetching task details",
          (
            error as {
              message: string;
            }
          )?.message
        );
      } finally {
        setFetchingJiraTask(false);
      }
    };

    const handleIssuesRefetch = () => {
      setIssueRefetch(true);
    }

    const getIssueCommands = async () => {
        if (projectType === projectTypeMenu[0]) {
            await getOpenProjectCommands()
        } else if (projectType === projectTypeMenu[2]) {
            getSelectedTaskDetails();
        } else {
            await getWaymoreCommands();
        }
    }

    useEffect(() => {
        getIssueCommands()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issueDetails])

    useEffect(() => {
        setTabValue(0);
    }, [issueDetails.id])

    const handleAdditionalInfo = async () => {
        setFullScreenLoading(true);
        const feedBackActionsResp = await getIssueActions(issueDetails.id);

        if (feedBackActionsResp && feedBackActionsResp.statusCode === 200) {
            if (feedBackActionsResp.data && feedBackActionsResp.data.data && feedBackActionsResp.data.data.length) {
                setFeedBackActions(feedBackActionsResp.data.data);
                setAdditionalInfoDialogOpen(true);
                setFullScreenLoading(false);
            } else {
                setFullScreenLoading(false);
                toast.error('Feedback Options is not available for this task');
            }
        } else {
            setFullScreenLoading(false);
            toast.error('User is not authorized to fetch feedback template');
        }
    }


    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setCommentText('');
        setTabValue(newValue);
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            if (scrollableDivRef.current) {
                const div: any = scrollableDivRef.current;
                div.scrollTop = div.scrollHeight;
            }
        }, 200);
    }

    const addCommentForOpenProjectIssue = async (values: { comment: string }) => {
        const commentPayload = {
            id: issueDetails?.id,
            comment: {
                raw: values?.comment
            }
        };
        try {
            let response = await addOpenProjectComment(commentPayload);
            if (response?.statusCode === 200) {

                let updatedIssueData = { ...issueDetails?.issueData };
                let newComments = (updatedIssueData.commentList && updatedIssueData.commentList.length > 0) ? [...updatedIssueData.commentList] : [];

                const newComment = response.data;
                if (newComment) {
                    newComments.push(newComment);
                }

                updatedIssueData.commentList = newComments;
                dispatch(setNewComments(updatedIssueData));
                scrollToBottom();
                toast.success('Comment added successfully');
            } else {
                toast.error('Failed to add Comment');
            }
        } catch (error) {
            toast.error('Failed to add Comment');
        } finally {
            setCommentText('');
        }
    }

    const addCommentForJiraIssue = async (values: { comment: string }) => {
        const payload = {
            issueKey: issueDetails?.key,
            body: values.comment
        }
        try {
            let response = await addJiraCommentForTask(payload);
            if (response?.statusCode === 200) {

                let updatedIssueData = { ...issueDetails?.issueData };
                let newComments = (updatedIssueData.comments && updatedIssueData.comments.length > 0) ? [...updatedIssueData.comments] : [];

                const newComment = response.data?.data;
                if (newComment) {
                    newComments.push(newComment);
                }

                updatedIssueData.comments = newComments;
                dispatch(setNewComments(updatedIssueData));
                scrollToBottom();
                toast.success('Comment Added successfully');
            } else {
                toast.error('Adding Comment Failed');
            }
        } catch (error) {
            toast.error('Adding Comment Failed');
        } finally {
            setCommentText('');
        }
    }

    const handleAddComment = async () => {
        if (commentText && commentText.length) {
            let payload = {
                comment: commentText
            }
            if (projectType === projectTypeMenu[0]) {
                await addCommentForOpenProjectIssue(payload);
            } else if (projectType === projectTypeMenu[2]) {
                await addCommentForJiraIssue(payload);
            }
            setMentionMapping({})
        }
    }

    const IsEpic = (): boolean => {
        // For Open Project
        if (issueDetails?.issueData?._links?.type?.title === "Epic") {
            return true;
        }
        // For Jira
        if (issueDetails?.issueData?.fields?.issuetype?.name === "Epic") {
            return true;
        }
        return false
    }

    const IsSubTask = (): boolean => {
      // For Open Project
      if (issueDetails?.issueData?._links?.type?.title?.toLowerCase().includes("sub")) {
        return true;
      }
      // For Jira
      if (issueDetails?.issueData?.fields?.issuetype?.subtask || issueDetails?.issueData?.fields?.issuetype?.name?.toLowerCase().includes("sub")) {
          return true;
      }
      return false;
    }

    const processStringWithLinks = (str: string = ""): React.ReactNode[] => {

        const urlRegex = /(https?:\/\/[^\s]+)/g;

        return str.split(urlRegex).map((part, index) => {
            if (part.match(urlRegex)) {
                return <a href={part} key={index} target="_blank" rel="noopener noreferrer">{part}</a>;
            }

            return part.split('\n').map((line, lineIndex) => (
                <React.Fragment key={`${index}-${lineIndex}`}>
                    {line}
                    {(lineIndex < part.split('\n').length - 1) && <br />}
                </React.Fragment>
            ));
        });
    };

    const processStringWithImage = (str: string = "") => {
        const regex = /->.*?<-/g;
        return str.split(regex).map((part, index) => {
            if (part.match(regex)) {
                return <img src={part} alt="" width={200} height={200} style={{ margin: '5px' }} />;
            }

            return part.split('\n').map((line, lineIndex) => (
                <React.Fragment key={`${index}-${lineIndex}`}>
                    {line}
                    {(lineIndex < part.split('\n').length - 1) && <br />}
                </React.Fragment>
            ));
        });
    }

    const processStringWithLinksAndImages = (str: string = ""): React.ReactNode[] => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const imageRegex = /->.*?<-/g;

        return str.split(urlRegex).map((part, index) => {
            if (part.match(urlRegex)) {
                return <a href={part} key={index} target="_blank" rel="noopener noreferrer">{part}</a>;
            } else {
                return part.split(imageRegex).map((imgPart, imgIndex) => {
                    if (imgPart.match(imageRegex)) {
                        const imageUrl: any = imgPart.split('->')[1].slice(0, -2);
                        return <img src={imageUrl} key={index} alt="" width={200} height={200} style={{ margin: '5px' }} />
                    }

                    return imgPart.split('\n').map((line, lineIndex) => (
                        <React.Fragment key={`${index}-${lineIndex}`}>
                            {line}
                            {(lineIndex < part.split('\n').length - 1) && <br />}
                        </React.Fragment>
                    ));
                })
                // if()
            }
        })
    };

    useEffect(() => {
        if (projectTypeMenu[0] === projectType) {
            searchOpenProjectUser("").then(userRes => {
                if (userRes?.data?.length) {
                    const matches = userRes.data.map((user: any) => ({
                        id: user.id,
                        display: user.name,
                        avatar: user?.avatar,
                    }));
                    setOpenProjectUserlist(matches)
                }
            })
        }
    }, [projectType])

    useEffect(() => {
        if(selectedNotification) {
            if (selectedNotification?.reason === "mentioned") {
                setTabValue(1)
            } else {
                setTabValue(0)
            }
        }
    }, [selectedNotification, issueDetails])

    const selectedOPTaskAssignee =
      issueDetails?.issueData?._links?.assignee?.href ===
      userState?.user?.openProjectDetails?._links?.self?.href
        ? userState?.user?.openProjectDetails
        : {
            _type: "User",
            name: issueDetails?.issueData?._links?.assignee?.title,
            email: "",
            avatar: "",
            _links: {
              self: issueDetails?.issueData?._links?.assignee,
            },
          };

    return (
      <div className="issue-detial" ref={containerRef}>
        {issueDetails.issue ? (
          <div className="issueDetailsDiv">
            <div
              style={{ fontWeight: "600", width: "100%" }}
              className="issueTimings"
            >
              <span>{issueDetails.issue}</span>
              <IconButton
                onClick={toggleFullScreen}
                size="small"
                aria-label={
                  isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"
                }
              >
                {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </div>
            {loading ? (
              <Box sx={{ width: "100%" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleChange}
                    centered
                    aria-label="basic tabs example"
                    sx={{ height: "30px", minHeight: "30px" }}
                  >
                    {["Task Details", "Comments", "Meeting Details"].map(
                      (label, index) => (
                        <Tab
                          key={index}
                          label={label}
                          {...a11yProps(index + 1)}
                          sx={{
                            padding: "4px 24px 2px 24px",
                            minHeight: "24px",
                            minWidth: "50px",
                            fontSize: "12px",
                            height: "24px",
                          }}
                        />
                      )
                    )}
                  </Tabs>
                </Box>
                <CustomTabPanel value={tabValue} index={0}>
                  <div className="issue-description">
                    <div>
                      <div style={{ width: "100%", paddingTop: '12px' }} className="issueTimings">
                        <span>
                          <b>Updated At:</b> {issueDetails.updatedAt}
                        </span>
                        <span>
                          <b>Due Date:</b> {issueDetails.dueDate}
                        </span>
                        <span
                          style={{
                            minWidth: "200px",
                            marginRight: "1rem",
                          }}
                        >
                          {selectedTaskDetails?.key &&
                            !fetchingJiraTask &&
                            projectType === projectTypeMenu[2] && (
                              <JiraAssigneeSelector
                                containerRef={containerRef}
                                label="Assignee"
                                assignee={selectedTaskDetails?.fields?.assignee}
                                issueId={selectedTaskDetails.key}
                                onAssigneeUpdated={handleIssuesRefetch}
                              />
                            )}
                          {issueDetails?.workPackagesLink &&
                            userState?.user?.openProjectDetails?.id &&
                            projectType === projectTypeMenu[0] && (
                              <OpenProjectAssigneeSelector
                                containerRef={containerRef}
                                label="Assignee"
                                assignee={
                                  selectedOPTaskAssignee as unknown as IOpenProjectUserDetails
                                }
                                issueInfo={{
                                  href: issueDetails?.workPackagesLink,
                                  lockVersion: issueDetails?.issueData
                                    ?.lockVersion as number,
                                }}
                                projectId={
                                  projectState?.selectedProject?.id ??
                                  issueDetails?.projectLink
                                    ?.split("/")
                                    ?.pop() ??
                                  ""
                                }
                                onAssigneeUpdated={handleIssuesRefetch}
                              />
                            )}
                        </span>
                      </div>
                      <span className="issue-field-title">Description :</span>
                    </div>
                    <div className="issue-description-contant">
                      {projectType === projectTypeMenu[0] &&
                      issueDetails?.descriptionHTML ? (
                        <HtmlContentRenderer
                          htmlString={issueDetails?.descriptionHTML ?? ""}
                        />
                      ) : (
                        <>
                           {fetchingJiraTask ? (
                            <Box sx={{ width: "fit-content", padding: "6px" }}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <AttachmentProvider>
                              <JiraIssueDescriptionRenderer
                                containerRef={containerRef}
                                issueData={selectedTaskDetails!}
                              />
                            </AttachmentProvider>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CustomTabPanel>
                {tabValue === 1 && (
                  <div className="add-comment-div">
                    <MentionsInput
                      className={`add-comment ${
                        matchLength > 1 ? "half" : "quarter"
                      }`}
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(event: any) =>
                        setCommentText(event.target.value)
                      }
                      a11ySuggestionsListLabel="User suggestions"
                      singleLine={false}
                      style={{
                        minHeight: "40px",
                        maxHeight: "400px",
                      }}
                    >
                      <Mention
                        trigger="@"
                        markup={
                          projectType === projectTypeMenu[0]
                            ? '<a href="/users/__id__">__display__</a>'
                            : projectType === projectTypeMenu[2]
                            ? "[~accountid:__id__]"
                            : ""
                        } // Stores the account ID internally
                        displayTransform={(id, display) => {
                          // Use stored display name if available
                          const resolvedDisplay =
                            mentionMapping[id] || display || id;
                          return `@${resolvedDisplay}`;
                        }}
                        data={async (query, callback) => {
                          try {
                            if (projectType === projectTypeMenu[0]) {
                              if (openProjectUserlist.length) {
                                const matches = openProjectUserlist.filter(
                                  (user: any) =>
                                    user?.display
                                      ?.toLowerCase()
                                      ?.trim()
                                      ?.includes(query?.trim()?.toLowerCase())
                                );
                                console.log(matches, "matches----");
                                setMatchLength(matches.length);
                                callback(matches);
                              } else {
                                setMatchLength(0);
                                callback([]);
                              }
                            } else if (projectType === projectTypeMenu[2]) {
                              const userRes = await getJiraUser({
                                text: query,
                              });
                              if (userRes?.data?.length) {
                                const matches = userRes.data.map(
                                  (user: any) => ({
                                    id: user.accountId, // Ensure this is the Jira account ID
                                    display: user.displayName, // Ensure this is the readable name
                                    avatar: user.avatarUrls["16x16"],
                                  })
                                );
                                setMatchLength(matches.length);
                                callback(matches);
                              } else {
                                setMatchLength(0);
                                callback([]);
                              }
                            } else {
                              callback([]);
                            }
                          } catch (err) {
                            console.error("Error fetching mentions:", err);
                            callback([]);
                          }
                        }}
                        appendSpaceOnAdd={true}
                        onAdd={handleMentionAdd}
                        style={{
                          backgroundColor: "#42526e33",
                          borderRadius: "5px",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                        className="test"
                        renderSuggestion={(
                          suggestion: any,
                          search,
                          highlightedDisplay,
                          index,
                          focused
                        ) => (
                          <div
                            className={`mentions__suggestions__item ${
                              focused
                                ? "mentions__suggestions__item--focused"
                                : ""
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "10px",
                              background: focused ? "#ececece5" : "#fff",
                              borderLeft: "4px solid #808080",
                            }}
                          >
                            <img
                              src={suggestion.avatar}
                              alt=""
                              onError={(e) =>
                                (e.currentTarget.src = defaultAvatar)
                              }
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                marginRight: "5px",
                              }}
                            />
                            {highlightedDisplay}
                          </div>
                        )}
                      />
                    </MentionsInput>

                    <SendIcon color="primary" onClick={handleAddComment} />
                  </div>
                )}
                <CustomTabPanel value={tabValue} index={1}>
                  <div className="issue-command">
                    <div
                      className="issue-command-contant"
                      ref={scrollableDivRef}
                    >
                      {projectType === projectTypeMenu[0] && (
                        <OPCommentSection comments={commandsList} />
                      )}
                      {projectType === projectTypeMenu[2] && (
                        <>
                        {fetchingJiraComment ? (
                            <Box
                              sx={{
                                width: "100%",
                                padding: "14px",
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          ) : (
                            <AttachmentProvider>
                              <CommentsSection
                                issueData={selectedTaskDetails!}
                                commentDetails={jiraTaskComments}
                              />
                            </AttachmentProvider>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={2}>
                  <div className="issue-meet">
                    <div
                      className="issue-command-contant"
                      ref={scrollableDivRef}
                    >
                      {meetScheduleList.length === 0 && (
                        <div
                          style={{ textAlign: "center", paddingTop: "12px" }}
                        >
                          No Meetings scheduled
                        </div>
                      )}
                      {meetScheduleList?.map((command, index) => {
                        return (
                          <div key={index} className="issue-meet-item">
                            {processStringWithLinks(command?.content || "")}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CustomTabPanel>
              </Box>
            )}
          </div>
        ) : (
          <div className="issueDetailsDiv">No Issue Selected</div>
        )}

        {issueDetails.issue
          ? showTaskFeature && (
              <>
                <div className="task-feature-button-container">
                {[
                  ...(projectType === projectTypeMenu[2]
                    ? [{ label: "Additional Info", onClick: handleAdditionalInfo }]
                    : []
                  ),
                  // {
                  //   label: "Create Documentation",
                  //   onClick: () => dispatch(setOpenCreateDocumentation(true)),
                  // },
                  { label: "Update Status", onClick: openUpdateStatusDialog },
                  {
                    label: "Create Sub Task",
                    onClick: () => {
                      setBlockParent(false);
                      openCreateSubTaskDialog();
                    },
                    disabled: IsSubTask() || IsEpic(),
                  },
                  // { label: "Task Not Relevant To Me" },
                  { label: "Add Meeting", onClick: openAddMeetingDialog },
                ].map(({ label, onClick, disabled }, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    onClick={onClick}
                    disabled={disabled}
                    sx={{
                      textTransform: "none",
                      minWidth: "max-content",
                      padding: "12px",
                      minHeight: "35px",
                      height: "30px",
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              </>
            )
          : selectedProject && (
              <div>
                <Button variant="contained" onClick={openAddMeetingDialog}>
                  Add Meeting
                </Button>
              </div>
            )}

            <AdditionalInfoTaskDialog isOpen={additionalInfoDialogOpen} onClose={closeAdditionalInfoDialog} onCreateSubTask={() => { setBlockParent(true); openCreateSubTaskDialog() }} feedBackOptions={feedBackActions} />
            <UpdateTaskStatusDialog isOpen={updateStatusDialogOpen} onClose={closeUpdateStatusDialog} />
            <CreateJiraTaskDialog isSubTask={!IsEpic()} isOpen={createJiraSubTaskDialogOpen} onClose={closeCreateSubTaskDialog} blockParent={blockParent} />
            {createOpenProjectSubTaskDialogOpen && <OpenProjectCreateTaskDialog isSubTask={!IsEpic()} isOpen={createOpenProjectSubTaskDialogOpen} onClose={closeCreateSubTaskDialog} />}
            <AddMeetingDialog isOpen={addMeetingDialogOpen} onClose={closeAddMeetingDialog} />

            <Backdrop open={fullScreenLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div >

    )
}

export default IssueDetailsPage

